import { useState } from 'react';
import { alert } from '@/services/alert';

export const MAX_TWILIO_ATTACHMENTS = 10;
export const MAX_TWILIO_BUNDLE_SIZE_MB = 5;
export const MAX_TWILIO_BUNDLE_SIZE_BYTES = MAX_TWILIO_BUNDLE_SIZE_MB * 1024 * 1024;

/**
 * Custom hook for managing file selection with optional attachment validation.
 * Handles file state and validates file count and size limits if provided.
 *
 * @param maxAttachments Optional maximum number of files allowed. If undefined/null, no limit.
 * @param maxBundleSizeBytes Optional maximum total size in bytes. If undefined/null, no limit.
 * @returns Object containing files state, setFiles function, and handleSelectFiles function
 */
export function useFileSelection({
  maxAttachments = MAX_TWILIO_ATTACHMENTS,
  maxBundleSizeBytes = MAX_TWILIO_BUNDLE_SIZE_BYTES,
}: {
  maxAttachments?: number | null;
  maxBundleSizeBytes?: number | null;
} = {}) {
  const [files, setFiles] = useState<File[]>([]);

  const handleSelectFiles = (selectedFiles: File[]) => {
    if (maxAttachments && selectedFiles.length > maxAttachments) {
      setFiles([]);
      alert.error({
        title: 'Error - Too many files',
        message: `You can upload up to ${maxAttachments} files per message.`,
      });
      return;
    }

    if (maxBundleSizeBytes && selectedFiles.reduce((acc, file) => acc + file.size, 0) > maxBundleSizeBytes) {
      setFiles([]);
      alert.error({
        title: 'Error: File bundle too large',
        message: `Total file size exceeds limit of ${Math.round((maxBundleSizeBytes || 0) / 1024 / 1024)}MB.`,
      });
      return;
    }

    setFiles(selectedFiles);
  };

  const removeFile = (indexToRemove: number) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const clearFiles = () => {
    setFiles([]);
  };

  return {
    files,
    setFiles,
    handleSelectFiles,
    removeFile,
    clearFiles,
  };
}
